import React, { useState, useEffect } from 'react';
import {
  FaTimes,
  FaSave,
  FaSpinner,
  FaFileInvoice,
  FaBuilding,
  FaMoneyBillWave,
  FaReceipt,
  FaEdit,
} from 'react-icons/fa';
import { Billing, BillingStatus, PoStatus, WccStatus, InvoiceStatus } from './index';

interface BillingFormData {
  project_site_id: number;
  billing_status: BillingStatus;
  is_po_received: boolean;
  billing_amount: number;
  billed_ref_id: string;
  billed_po_number: string;
  billed_po_date: string;
  billed_po_status: PoStatus;
  wcc_number: string;
  wcc_date: string;
  wcc_status: WccStatus;
  receipt_number: string;
  invoice_status: InvoiceStatus;
  invoice_number: string;
  invoice_date: string;
  invoice_amount: number;
}

interface EditBillingOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: BillingFormData) => Promise<void>;
  billing: any;
  loading?: boolean;
}

export const EditBillingOffcanvas: React.FC<EditBillingOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  billing,
  loading = false,
}) => {
  const [formData, setFormData] = useState<any>({
    project_site_id: 0,
    billing_status: BillingStatus.PENDING,
    is_po_received: false,
    billing_amount: 0,
    billed_ref_id: '',
    billed_po_number: '',
    billed_po_date: new Date().toISOString().split('T')[0],
    billed_po_status: PoStatus.PENDING,
    wcc_number: '',
    wcc_date: new Date().toISOString().split('T')[0],
    wcc_status: WccStatus.PENDING,
    receipt_number: '',
    invoice_status: InvoiceStatus.DRAFT,
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    invoice_amount: 0,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof BillingFormData, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof BillingFormData, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load billing data when modal opens
  useEffect(() => {
    if (isOpen && billing) {
      const formatDateForInput = (date: Date | string) => {
        if (!date) return new Date().toISOString().split('T')[0];
        const d = new Date(date);
        return d.toISOString().split('T')[0];
      };

      setFormData({
        project_site_id: billing.project_site_id,
        billing_status: billing.billing_status,
        is_po_received: billing.is_po_received,
        billing_amount: billing.billing_amount,
        billed_ref_id: billing.billed_ref_id,
        billed_po_number: billing.billed_po_number,
        billed_po_date: formatDateForInput(billing.billed_po_date),
        billed_po_status: billing.billed_po_status,
        wcc_number: billing.wcc_number,
        wcc_date: formatDateForInput(billing.wcc_date),
        wcc_status: billing.wcc_status,
        receipt_number: billing.receipt_number,
        invoice_status: billing.invoice_status,
        invoice_number: billing.invoice_number,
        invoice_date: formatDateForInput(billing.invoice_date),
        invoice_amount: billing.invoice_amount,
      });
      setErrors({});
      setTouched({});
    }
  }, [isOpen, billing]);

  // Validation
  const validateField = (name: keyof BillingFormData, value: any): string => {
    switch (name) {
      case 'project_site_id':
        if (!value || value <= 0) return 'Project site is required';
        return '';
      case 'billing_amount':
        if (!value || value <= 0) return 'Billing amount must be greater than 0';
        return '';
      case 'invoice_amount':
        if (!value || value <= 0) return 'Invoice amount must be greater than 0';
        return '';
      case 'billed_ref_id':
        if (!value.trim()) return 'Reference ID is required';
        if (value.trim().length > 100) return 'Reference ID cannot exceed 100 characters';
        return '';
      case 'billed_po_number':
        if (!value.trim()) return 'PO number is required';
        if (value.trim().length > 100) return 'PO number cannot exceed 100 characters';
        return '';
      case 'wcc_number':
        if (!value.trim()) return 'WCC number is required';
        if (value.trim().length > 100) return 'WCC number cannot exceed 100 characters';
        return '';
      case 'receipt_number':
        if (!value.trim()) return 'Receipt number is required';
        if (value.trim().length > 100) return 'Receipt number cannot exceed 100 characters';
        return '';
      case 'invoice_number':
        if (!value.trim()) return 'Invoice number is required';
        if (value.trim().length > 100) return 'Invoice number cannot exceed 100 characters';
        return '';
      case 'billed_po_date':
      case 'wcc_date':
      case 'invoice_date':
        if (!value) return 'Date is required';
        return '';
      default:
        return '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};
    let isValid = true;

    Object.keys(formData).forEach((key) => {
      const fieldKey = key as keyof BillingFormData;
      const error = validateField(fieldKey, formData[fieldKey]);
      if (error) {
        newErrors[fieldKey] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Handle input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const fieldName = name as keyof BillingFormData;

    let finalValue: any = value;

    if (type === 'number') {
      finalValue = parseFloat(value) || 0;
    } else if (type === 'checkbox') {
      finalValue = (e.target as HTMLInputElement).checked;
    }

    setFormData((prev: any) => ({
      ...prev,
      [fieldName]: finalValue,
    }));

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: '',
      }));
    }

    // Mark field as touched
    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };

  // Handle blur events for validation
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof BillingFormData;

    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));

    const error = validateField(fieldName, value);
    setErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched: any = {};
    Object.keys(formData).forEach((key) => {
      allTouched[key as keyof BillingFormData] = true;
    });
    setTouched(allTouched);

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        await onSubmit(formData);
      } catch (error) {
        // Error is handled by parent component
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={!isSubmitting ? onClose : undefined}
      />

      {/* Offcanvas */}
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FaEdit className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Edit Billing</h2>
              <p className="text-sm text-gray-600">Update billing record #{billing.id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            disabled={isSubmitting}
          >
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 p-4 rounded">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FaBuilding className="w-4 h-4" />
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Project Site ID */}
                <div>
                  <label htmlFor="project_site_id" className="block text-sm font-medium text-gray-700 mb-2">
                    Project Site ID *
                  </label>
                  <input
                    type="number"
                    id="project_site_id"
                    name="project_site_id"
                    value={formData.project_site_id}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.project_site_id && touched.project_site_id
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    placeholder="Enter project site ID"
                  />
                  {errors.project_site_id && touched.project_site_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.project_site_id}</p>
                  )}
                </div>

                {/* Billing Status */}
                <div>
                  <label htmlFor="billing_status" className="block text-sm font-medium text-gray-700 mb-2">
                    Billing Status *
                  </label>
                  <select
                    id="billing_status"
                    name="billing_status"
                    value={formData.billing_status}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                  >
                    <option value={BillingStatus.PENDING}>Pending</option>
                    <option value={BillingStatus.WCC_RAISED}>WCC Raised</option>
                    <option value={BillingStatus.WCC_APPROVED}>WCC Approved</option>
                    <option value={BillingStatus.INVOICE_RAISED}>Invoice Raised</option>
                    <option value={BillingStatus.INVOICE_SUBMITTED}>Invoice Submitted</option>
                    <option value={BillingStatus.PAID}>Paid</option>
                  </select>
                </div>

                {/* Reference ID */}
                <div>
                  <label htmlFor="billed_ref_id" className="block text-sm font-medium text-gray-700 mb-2">
                    Reference ID *
                  </label>
                  <input
                    type="text"
                    id="billed_ref_id"
                    name="billed_ref_id"
                    value={formData.billed_ref_id}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.billed_ref_id && touched.billed_ref_id
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    placeholder="Enter reference ID"
                    maxLength={100}
                  />
                  {errors.billed_ref_id && touched.billed_ref_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.billed_ref_id}</p>
                  )}
                </div>

                {/* Billing Amount */}
                <div>
                  <label htmlFor="billing_amount" className="block text-sm font-medium text-gray-700 mb-2">
                    Billing Amount (₹) *
                  </label>
                  <input
                    type="number"
                    id="billing_amount"
                    name="billing_amount"
                    value={formData.billing_amount}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    step="0.01"
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.billing_amount && touched.billing_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    placeholder="Enter billing amount"
                  />
                  {errors.billing_amount && touched.billing_amount && (
                    <p className="mt-1 text-sm text-red-600">{errors.billing_amount}</p>
                  )}
                </div>
              </div>
            </div>

            {/* PO Details */}
            <div className="bg-gray-50 p-4 rounded">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FaReceipt className="w-4 h-4" />
                Purchase Order Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* PO Received Checkbox */}
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="is_po_received"
                      checked={formData.is_po_received}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">PO Received</span>
                  </label>
                </div>

                {/* PO Number */}
                <div>
                  <label htmlFor="billed_po_number" className="block text-sm font-medium text-gray-700 mb-2">
                    PO Number *
                  </label>
                  <input
                    type="text"
                    id="billed_po_number"
                    name="billed_po_number"
                    value={formData.billed_po_number}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.billed_po_number && touched.billed_po_number
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    placeholder="Enter PO number"
                    maxLength={100}
                  />
                  {errors.billed_po_number && touched.billed_po_number && (
                    <p className="mt-1 text-sm text-red-600">{errors.billed_po_number}</p>
                  )}
                </div>

                {/* PO Date */}
                <div>
                  <label htmlFor="billed_po_date" className="block text-sm font-medium text-gray-700 mb-2">
                    PO Date *
                  </label>
                  <input
                    type="date"
                    id="billed_po_date"
                    name="billed_po_date"
                    value={formData.billed_po_date}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.billed_po_date && touched.billed_po_date
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                  />
                  {errors.billed_po_date && touched.billed_po_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.billed_po_date}</p>
                  )}
                </div>

                {/* PO Status */}
                <div>
                  <label htmlFor="billed_po_status" className="block text-sm font-medium text-gray-700 mb-2">
                    PO Status *
                  </label>
                  <select
                    id="billed_po_status"
                    name="billed_po_status"
                    value={formData.billed_po_status}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                  >
                    <option value={PoStatus.PENDING}>Pending</option>
                    <option value={PoStatus.RECEIVED}>Received</option>
                    <option value={PoStatus.REJECTED}>Rejected</option>
                  </select>
                </div>
              </div>
            </div>

            {/* WCC Details */}
            <div className="bg-gray-50 p-4 rounded">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">WCC Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* WCC Number */}
                <div>
                  <label htmlFor="wcc_number" className="block text-sm font-medium text-gray-700 mb-2">
                    WCC Number *
                  </label>
                  <input
                    type="text"
                    id="wcc_number"
                    name="wcc_number"
                    value={formData.wcc_number}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.wcc_number && touched.wcc_number
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    placeholder="Enter WCC number"
                    maxLength={100}
                  />
                  {errors.wcc_number && touched.wcc_number && (
                    <p className="mt-1 text-sm text-red-600">{errors.wcc_number}</p>
                  )}
                </div>

                {/* WCC Date */}
                <div>
                  <label htmlFor="wcc_date" className="block text-sm font-medium text-gray-700 mb-2">
                    WCC Date *
                  </label>
                  <input
                    type="date"
                    id="wcc_date"
                    name="wcc_date"
                    value={formData.wcc_date}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.wcc_date && touched.wcc_date
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                  />
                  {errors.wcc_date && touched.wcc_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.wcc_date}</p>
                  )}
                </div>

                {/* WCC Status */}
                <div>
                  <label htmlFor="wcc_status" className="block text-sm font-medium text-gray-700 mb-2">
                    WCC Status *
                  </label>
                  <select
                    id="wcc_status"
                    name="wcc_status"
                    value={formData.wcc_status}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                  >
                    <option value={WccStatus.PENDING}>Pending</option>
                    <option value={WccStatus.APPROVED}>Approved</option>
                    <option value={WccStatus.REJECTED}>Rejected</option>
                  </select>
                </div>

                {/* Receipt Number */}
                <div>
                  <label htmlFor="receipt_number" className="block text-sm font-medium text-gray-700 mb-2">
                    Receipt Number *
                  </label>
                  <input
                    type="text"
                    id="receipt_number"
                    name="receipt_number"
                    value={formData.receipt_number}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.receipt_number && touched.receipt_number
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    placeholder="Enter receipt number"
                    maxLength={100}
                  />
                  {errors.receipt_number && touched.receipt_number && (
                    <p className="mt-1 text-sm text-red-600">{errors.receipt_number}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="bg-gray-50 p-4 rounded">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FaMoneyBillWave className="w-4 h-4" />
                Invoice Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Invoice Number */}
                <div>
                  <label htmlFor="invoice_number" className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Number *
                  </label>
                  <input
                    type="text"
                    id="invoice_number"
                    name="invoice_number"
                    value={formData.invoice_number}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.invoice_number && touched.invoice_number
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    placeholder="Enter invoice number"
                    maxLength={100}
                  />
                  {errors.invoice_number && touched.invoice_number && (
                    <p className="mt-1 text-sm text-red-600">{errors.invoice_number}</p>
                  )}
                </div>

                {/* Invoice Date */}
                <div>
                  <label htmlFor="invoice_date" className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Date *
                  </label>
                  <input
                    type="date"
                    id="invoice_date"
                    name="invoice_date"
                    value={formData.invoice_date}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.invoice_date && touched.invoice_date
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                  />
                  {errors.invoice_date && touched.invoice_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.invoice_date}</p>
                  )}
                </div>

                {/* Invoice Amount */}
                <div>
                  <label htmlFor="invoice_amount" className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Amount (₹) *
                  </label>
                  <input
                    type="number"
                    id="invoice_amount"
                    name="invoice_amount"
                    value={formData.invoice_amount}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    step="0.01"
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.invoice_amount && touched.invoice_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    placeholder="Enter invoice amount"
                  />
                  {errors.invoice_amount && touched.invoice_amount && (
                    <p className="mt-1 text-sm text-red-600">{errors.invoice_amount}</p>
                  )}
                </div>

                {/* Invoice Status */}
                <div>
                  <label htmlFor="invoice_status" className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Status *
                  </label>
                  <select
                    id="invoice_status"
                    name="invoice_status"
                    value={formData.invoice_status}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                  >
                    <option value={InvoiceStatus.DRAFT}>Draft</option>
                    <option value={InvoiceStatus.SUBMITTED}>Submitted</option>
                    <option value={InvoiceStatus.APPROVED}>Approved</option>
                    <option value={InvoiceStatus.PAID}>Paid</option>
                    <option value={InvoiceStatus.CANCELLED}>Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-amber-50 border border-amber-200 rounded p-4">
              <div className="flex items-start gap-3">
                <FaFileInvoice className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">Update Guidelines</p>
                  <ul className="text-xs space-y-1 list-disc list-inside">
                    <li>Review all changes carefully before saving</li>
                    <li>Status changes may affect downstream processes</li>
                    <li>Ensure amounts match supporting documents</li>
                    <li>Contact finance team for major changes</li>
                  </ul>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-6 py-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-950 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <FaSave className="w-4 h-4" />
                  Update Billing
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};